import React from 'react';
import { generateClassNameByBehaviorSettings } from '../../../utils/behaviorSettings';
import { DefaultPictureType, LinkWithLabel } from '../../../types';
import Picture from '../../../components/01_atoms/DefaultPicture';
import FormattedText from '../../../components/01_atoms/FormattedText';
import Link from '../../Link';
import Button from '../../Button';
import useScreenSize from '../../../hooks/useScreenSize';
import { BehaviourSettingsType } from '../../../utils/behaviorSettings/prop-types';

import styles from './index.module.scss';

type Props = {
  image?: DefaultPictureType;
  heading: string;
  subheading?: string;
  button: LinkWithLabel;
  secondaryButton?: LinkWithLabel;
  style?: 'default' | 'christmas';
  behaviorSettings?: BehaviourSettingsType;
  uuid?: string;
};

const BBInlineCta = ({
  image,
  heading = '',
  subheading = '',
  button,
  secondaryButton,
  style = 'default',
  behaviorSettings,
  uuid,
}: Props) => {
  const { width } = useScreenSize();
  const isMobile = width && width < 768;

  const classes = [
    'bb',
    styles['bb-inline-cta'],
    styles[`bb-inline-cta--${style}`],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  const textSection = (
    <div className={styles['bb-inline-cta__text']}>
      <FormattedText tag="h2" text={heading} className={styles['bb-inline-cta__heading']} />

      {!!subheading && (
        <FormattedText text={subheading} className={styles['bb-inline-cta__subheading']} />
      )}
    </div>
  );

  const buttonSection = (
    <div className={styles['bb-inline-cta__button']}>
      {button && button.nextLink && (
        <Link {...button.nextLink}>
          <Button
            type={style === 'christmas' ? 'xmas' : 'primary'}
            size={secondaryButton?.nextLink ? 'large' : 'extra-large'}
            withArrow={!secondaryButton?.nextLink}
            data-button-type="primary"
          >
            {button.label}
          </Button>
        </Link>
      )}
      {secondaryButton && secondaryButton.nextLink && (
        <Link {...secondaryButton.nextLink}>
          <Button type="xmas" size="large" data-button-type="secondary" isOutlined>
            {secondaryButton.label}
          </Button>
        </Link>
      )}
    </div>
  );

  const hasSecondaryButton = secondaryButton && secondaryButton.nextLink;

  return (
    <div className={classes.join(' ')} id={uuid} data-component="bb-inline-cta">
      <div className="container">
        <div
          className={`${styles['bb-inline-cta__content']} ${hasSecondaryButton && styles['bb-inline-cta__content-centered']}`}
        >
          {!isMobile && image && (
            <div
              className={`${styles['bb-inline-cta__image']} ${hasSecondaryButton && styles['bb-inline-cta__image--top']}`}
            >
              <Picture {...image} />
            </div>
          )}
          {hasSecondaryButton ? (
            <div className={styles['bb-inline-cta__content-wrapper']}>
              {textSection}
              {buttonSection}
            </div>
          ) : (
            <>
              {textSection}
              {buttonSection}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BBInlineCta;
